import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Check, ArrowLeft, Minus, Plus, CheckCircle, AlertTriangle, XCircle, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import BulkDiscountCalculator from '../components/BulkDiscountCalculator';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const data = await productsAPI.getById(id);
        setProduct(data);
        setActiveImageIdx(0);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <Button onClick={() => navigate('/products')}>Back to Products</Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
    const savings = discountInfo?.savings || 0;
    const message = savings > 0
      ? `${quantity} x ${product.name} added to cart! You save KES ${savings.toLocaleString()}`
      : `${quantity} x ${product.name} added to cart!`;
    toast.success(message);
  };

  const increaseQuantity = () => setQuantity(prev => prev + 1);
  const decreaseQuantity = () => setQuantity(prev => (prev > 1 ? prev - 1 : 1));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Product Image / Slider */}
          {(() => {
            const gallery = (product.images && product.images.length > 0)
              ? product.images
              : [product.image];
            const hasMultiple = gallery.length > 1;
            const goPrev = () => setActiveImageIdx((i) => (i - 1 + gallery.length) % gallery.length);
            const goNext = () => setActiveImageIdx((i) => (i + 1) % gallery.length);
            const current = gallery[activeImageIdx] || gallery[0];
            const isVideo = (src) => typeof src === 'string' && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(src);

            return (
              <div className="bg-white rounded-lg p-6">
                <div className="relative" data-testid="product-image-slider">
                  {isVideo(current) ? (
                    <video
                      key={current}
                      src={current}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full h-96 object-contain rounded-lg bg-gray-50"
                      data-testid="product-video"
                    />
                  ) : (
                    <img
                      src={current}
                      alt={`${product.name} - view ${activeImageIdx + 1}`}
                      className="w-full h-96 object-contain rounded-lg bg-gray-50"
                    />
                  )}
                  {hasMultiple && (
                    <>
                      <button
                        type="button"
                        onClick={goPrev}
                        aria-label="Previous"
                        data-testid="slider-prev-btn"
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-full p-2 border border-gray-200 transition"
                      >
                        <ChevronLeft className="h-5 w-5 text-gray-700" />
                      </button>
                      <button
                        type="button"
                        onClick={goNext}
                        aria-label="Next"
                        data-testid="slider-next-btn"
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-md rounded-full p-2 border border-gray-200 transition"
                      >
                        <ChevronRight className="h-5 w-5 text-gray-700" />
                      </button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2 py-1 rounded-full pointer-events-none">
                        {activeImageIdx + 1} / {gallery.length}
                      </div>
                    </>
                  )}
                </div>

                {hasMultiple && (
                  <div className="mt-4 flex gap-2 overflow-x-auto" data-testid="slider-thumbnails">
                    {gallery.map((src, idx) => (
                      <button
                        key={src + idx}
                        type="button"
                        onClick={() => setActiveImageIdx(idx)}
                        aria-label={`Show item ${idx + 1}`}
                        data-testid={`slider-thumb-${idx}`}
                        className={`relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition ${
                          idx === activeImageIdx ? 'border-orange-600' : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        {isVideo(src) ? (
                          <>
                            <video
                              src={src}
                              muted
                              playsInline
                              preload="metadata"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                              <Play className="h-6 w-6 text-white drop-shadow" fill="white" />
                            </div>
                          </>
                        ) : (
                          <img src={src} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Product Details */}
          <div>
            <Badge className="mb-4">{product.category}</Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {product.name}
            </h1>
            
            {/* Stock Status with Quantity */}
            <div className="mb-6">
              {(() => {
                const totalStock = product.variants && product.variants.length > 0
                  ? product.variants.reduce((total, v) => total + (v.stock || 0), 0)
                  : 50;
                const isLowStock = totalStock > 0 && totalStock <= 10;
                const isOutOfStock = totalStock === 0;

                if (isOutOfStock) {
                  return (
                    <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg border border-red-200">
                      <XCircle className="h-5 w-5" />
                      <span className="font-semibold">Out of Stock</span>
                    </div>
                  );
                }
                if (isLowStock) {
                  return (
                    <div className="inline-flex items-center gap-2 bg-yellow-50 text-yellow-700 px-4 py-2 rounded-lg border border-yellow-200">
                      <AlertTriangle className="h-5 w-5" />
                      <span className="font-semibold">Only {totalStock} left in stock!</span>
                    </div>
                  );
                }
                return (
                  <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg border border-green-200">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-semibold">{totalStock} in stock</span>
                  </div>
                );
              })()}
            </div>

            <div className="mb-6">
              <p className="text-4xl font-bold text-gray-900 mb-2">
                KES {product.price.toLocaleString()}
              </p>
              <p className="text-gray-600">Per unit (Bulk discounts available)</p>
            </div>

            <p className="text-gray-700 mb-6 leading-relaxed">
              {product.description}
            </p>

            <Card className="mb-6">
              <CardContent className="p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Product Specifications</h3>
                <p className="text-sm text-gray-700">{product.specifications}</p>
              </CardContent>
            </Card>

            <Card className="mb-6 border-green-200 bg-green-50">
              <CardContent className="p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-2" />
                  Compliance & Certifications
                </h3>
                <p className="text-sm text-gray-700">{product.compliance}</p>
              </CardContent>
            </Card>

            {/* Quantity Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={decreaseQuantity}
                    className="px-3"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="px-6 py-2 font-semibold text-gray-900">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={increaseQuantity}
                    className="px-3"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="text-gray-600">
                  Total: <span className="font-bold text-gray-900">
                    KES {((product.price * quantity) - (discountInfo?.discount_amount || 0)).toLocaleString()}
                  </span>
                  {discountInfo?.savings > 0 && (
                    <span className="text-green-600 text-sm ml-2">
                      (Save KES {discountInfo.savings.toLocaleString()})
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Add to Cart Button */}
            <Button
              size="lg"
              onClick={handleAddToCart}
              className="w-full bg-orange-600 hover:bg-orange-700 mb-4"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              Add to Cart
            </Button>

            <div className="border-t pt-6 mt-6">
              <h3 className="font-semibold text-gray-900 mb-3">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-3">
                Contact us for bulk orders, custom branding, or project-specific requirements.
              </p>
              <a href="https://wa.me/254716504759" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full">
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Bulk Discount Calculator */}
        <div className="mt-8 max-w-2xl">
          <BulkDiscountCalculator
            quantity={quantity}
            unitPrice={product.price}
            onDiscountCalculated={setDiscountInfo}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
